import { useState, useRef, useEffect } from 'react';
import { useUI } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import { weekMondayFromOffset } from '@focus/shared';

const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export function TopBar() {
  const {
    view, setView,
    viewMode, setViewMode,
    weekOffset, weekNext, weekPrev, weekToday,
    dayOffset, goPrevDay, goNextDay, goToday,
    monthOffset, goPrevMonth, goNextMonth, goThisMonth,
    darkMode, toggleDark, openModal,
    zoomLevel, zoomIn, zoomOut, zoomReset,
    quietHoursEnabled, quietHoursFrom, quietHoursTo, toggleQuietHours, setQuietHours,
    mobilePanel, setMobilePanel,
  } = useUI();

  const [quietOpen, setQuietOpen] = useState(false);
  const quietRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!quietOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!quietRef.current) return;
      if (!quietRef.current.contains(e.target as Node)) setQuietOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [quietOpen]);

  let centerLabel = '';
  let onPrev: () => void = () => {};
  let onNext: () => void = () => {};
  let onCenter: () => void = () => {};
  let prevTitle = 'Zurück';
  let nextTitle = 'Vor';

  if (view === 'week') {
    const monday = weekMondayFromOffset(weekOffset);
    const kw = getKW(monday);
    centerLabel = `KW ${kw}`;
    onPrev = weekPrev; onNext = weekNext; onCenter = weekToday;
    prevTitle = 'Vorherige Woche'; nextTitle = 'Nächste Woche';
  } else if (view === 'day') {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    const dayIdx = (d.getDay() + 6) % 7;
    centerLabel = `${d.getDate()}. ${WEEKDAY_LONG[dayIdx]} ${d.getFullYear()}`;
    onPrev = goPrevDay; onNext = goNextDay; onCenter = goToday;
    prevTitle = 'Tag zurück'; nextTitle = 'Tag vor';
  } else {
    const base = new Date();
    const cur = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
    centerLabel = cur.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    onPrev = goPrevMonth; onNext = goNextMonth; onCenter = goThisMonth;
    prevTitle = 'Vorheriger Monat'; nextTitle = 'Nächster Monat';
  }

  const showZoom = view !== 'month';

  const viewSeg = (
    <div className="inline-flex items-center p-[3px] bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-full gap-[2px]">
      {(['day', 'week', 'month'] as const).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            onClick={() => setView(v)}
            className={cn(
              'h-6 px-3 rounded-full text-[12.5px] font-medium transition whitespace-nowrap',
              active
                ? 'bg-fg text-paper dark:bg-dark-fg dark:text-dark-bg'
                : 'text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt',
            )}
          >
            {v === 'day' ? 'Tag' : v === 'week' ? 'Woche' : 'Monat'}
          </button>
        );
      })}
    </div>
  );

  const viewModeSeg = (
    <div
      title="Plan: geplante Blöcke · Ist: tatsächliche Aktivität · Mix: Plan als Rahmen + Ist darauf"
      className="inline-flex items-center p-[3px] bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-full gap-[2px]"
    >
      {(['plan', 'actual', 'hybrid'] as const).map((m) => {
        const active = viewMode === m;
        return (
          <button
            key={m}
            onClick={() => setViewMode(m)}
            className={cn(
              'h-6 px-3 rounded-full text-[12.5px] font-medium transition whitespace-nowrap',
              active
                ? 'bg-fg text-paper dark:bg-dark-fg dark:text-dark-bg'
                : 'text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt',
            )}
          >
            {m === 'plan' ? 'Plan' : m === 'actual' ? 'Ist' : 'Mix'}
          </button>
        );
      })}
    </div>
  );

  const zoomCluster = showZoom && (
    <div className="flex items-center border border-line dark:border-dark-line rounded-md overflow-hidden h-7">
      <button
        onClick={zoomOut}
        disabled={zoomLevel <= 0.5}
        className="h-7 w-7 grid place-items-center hover:bg-bg dark:hover:bg-dark-bg disabled:opacity-40 disabled:cursor-not-allowed text-fg dark:text-dark-fg"
        title="Raus zoomen"
      >
        <Icon name="minus" size={13} />
      </button>
      <button
        onClick={zoomReset}
        className="h-7 min-w-[44px] px-1 text-[11px] font-mono tabular-nums border-x border-line dark:border-dark-line hover:bg-bg dark:hover:bg-dark-bg text-fg dark:text-dark-fg"
        title="Zoom zurücksetzen"
      >
        {Math.round(zoomLevel * 100)}%
      </button>
      <button
        onClick={zoomIn}
        disabled={zoomLevel >= 3}
        className="h-7 w-7 grid place-items-center hover:bg-bg dark:hover:bg-dark-bg disabled:opacity-40 disabled:cursor-not-allowed text-fg dark:text-dark-fg"
        title="Rein zoomen"
      >
        <Icon name="plus" size={13} />
      </button>
    </div>
  );

  const dateNav = (
    <div className="flex items-center gap-1 lg:gap-2">
      <button onClick={onPrev} className="btn-ghost h-9 w-9 lg:h-auto lg:w-auto justify-center lg:justify-start" title={prevTitle}>
        <Icon name="chevronLeft" size={15} />
      </button>
      <button
        onClick={onCenter}
        className="btn-ghost font-medium text-[12.5px] lg:min-w-[200px] justify-center tracking-[-0.1px] truncate max-w-[180px] lg:max-w-none"
        title="Heute"
      >
        {centerLabel}
      </button>
      <button onClick={onNext} className="btn-ghost h-9 w-9 lg:h-auto lg:w-auto justify-center lg:justify-start" title={nextTitle}>
        <Icon name="chevronRight" size={15} />
      </button>
    </div>
  );

  return (
    <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-1 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-0 lg:h-[54px] border-b border-line dark:border-dark-line bg-paper dark:bg-dark-paper pt-safe shrink-0">
      {/* Mobile row 1: Liste/Kalender + Datum-Nav + Theme/Settings */}
      <div className="flex items-center justify-between gap-2 lg:hidden">
        <div className="inline-flex items-center p-[3px] bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-full gap-[2px]">
          {(['todos', 'calendar'] as const).map((p) => {
            const active = mobilePanel === p;
            return (
              <button
                key={p}
                onClick={() => setMobilePanel(p)}
                className={cn(
                  'px-3 h-8 rounded-full text-[12.5px] font-medium tracking-[-0.1px] transition',
                  active
                    ? 'bg-fg text-paper dark:bg-dark-fg dark:text-dark-bg'
                    : 'text-fg dark:text-dark-fg hover:opacity-80',
                )}
              >
                {p === 'todos' ? 'Liste' : 'Kalender'}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={toggleDark} className="btn-ghost h-9 w-9 justify-center" title={darkMode ? 'Light Mode' : 'Dark Mode'}>
            <Icon name={darkMode ? 'sun' : 'moon'} size={16} />
          </button>
          <button onClick={() => openModal('settings')} className="btn-ghost h-9 w-9 justify-center" title="Einstellungen">
            <Icon name="settings" size={16} />
          </button>
        </div>
      </div>

      {/* Mobile row 2: Datum-Nav (KW/Tag/Monat label + arrows) zentriert */}
      <div className="flex items-center justify-center lg:hidden">
        {dateNav}
      </div>

      {/* Mobile row 3: View-Selector + Plan/Ist/Mix (nur wenn Kalender-Panel aktiv) */}
      {mobilePanel === 'calendar' && (
        <div className="flex items-center justify-between gap-2 lg:hidden overflow-x-auto scroll-thin">
          {viewSeg}
          <div className="flex items-center gap-1.5">
            {viewModeSeg}
            {zoomCluster}
          </div>
        </div>
      )}

      {/* Desktop layout */}
      <div className="hidden lg:flex items-center gap-3 min-w-0">
        <div className="inline-flex items-center gap-2 text-[13px] font-semibold tracking-[-0.2px] text-fg dark:text-dark-fg shrink-0">
          <span className="w-5 h-5 rounded-md bg-fg dark:bg-dark-fg text-paper dark:text-dark-bg inline-flex items-center justify-center text-[11px] leading-none">◐</span>
          <span className="hidden lg:inline">Focus Coach</span>
        </div>
        {viewSeg}
        {viewModeSeg}
      </div>

      <div className="hidden lg:flex items-center gap-2">
        <button onClick={onPrev} className="btn-ghost" title={prevTitle}>
          <Icon name="chevronLeft" size={15} />
        </button>
        <button
          onClick={onCenter}
          className="btn-ghost font-medium text-[12.5px] min-w-[200px] justify-center tracking-[-0.1px]"
          title="Heute"
        >
          {centerLabel}
        </button>
        <button onClick={onNext} className="btn-ghost" title={nextTitle}>
          <Icon name="chevronRight" size={15} />
        </button>
      </div>

      <div className="hidden lg:flex items-center gap-1">
        {showZoom && (
          <>
            {zoomCluster}
            <div ref={quietRef} className="relative hidden lg:block">
              <button
                onClick={() => setQuietOpen((v) => !v)}
                className={cn(
                  'btn-ghost',
                  quietHoursEnabled && 'text-accent dark:text-accent',
                )}
                title="Ruhezeiten ausblenden"
              >
                <Icon name="bed" size={15} />
              </button>
              {quietOpen && (
                <div className="absolute right-0 top-full mt-1 z-40 w-[260px] rounded-lg border border-line dark:border-dark-line bg-paper dark:bg-dark-paper shadow-pop p-3 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={quietHoursEnabled}
                      onChange={(e) => setQuietHours({ enabled: e.target.checked })}
                      className="h-4 w-4 accent-accent cursor-pointer"
                    />
                    <span className="text-[12.5px] font-medium tracking-[-0.1px] text-fg dark:text-dark-fg">
                      Ruhezeiten ausblenden
                    </span>
                  </label>
                  <div className={cn('space-y-2', !quietHoursEnabled && 'opacity-50 pointer-events-none')}>
                    <div className="flex items-center gap-2">
                      <label className="text-[11.5px] font-mono text-fg dark:text-dark-fg w-10">von</label>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={quietHoursFrom}
                        onChange={(e) => setQuietHours({ from: Number(e.target.value) })}
                        className="flex-1 h-8 px-2 rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent text-[12.5px] font-mono tabular-nums text-fg dark:text-dark-fg"
                      />
                      <span className="text-[11px] font-mono text-fg dark:text-dark-fg">Uhr</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-[11.5px] font-mono text-fg dark:text-dark-fg w-10">bis</label>
                      <input
                        type="number"
                        min={1}
                        max={24}
                        value={quietHoursTo}
                        onChange={(e) => setQuietHours({ to: Number(e.target.value) })}
                        className="flex-1 h-8 px-2 rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent text-[12.5px] font-mono tabular-nums text-fg dark:text-dark-fg"
                      />
                      <span className="text-[11px] font-mono text-fg dark:text-dark-fg">Uhr</span>
                    </div>
                    <div className="text-[10.5px] font-mono text-fg dark:text-dark-fg leading-snug">
                      Sichtbar: {String(quietHoursTo).padStart(2, '0')}:00 – {String(quietHoursFrom).padStart(2, '0')}:00
                    </div>
                  </div>
                  {quietHoursFrom <= quietHoursTo && quietHoursEnabled && (
                    <div className="text-[10.5px] text-bad dark:text-bad leading-snug">
                      „von" muss grösser sein als „bis". Beispiel: von 22, bis 6 zeigt 06:00–22:00.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <button onClick={() => openModal('briefing')} className="btn-ghost hidden lg:inline-flex" title="Briefing">
          <Icon name="sparkle" size={15} />
        </button>
        <button onClick={() => openModal('overview')} className="btn-ghost hidden lg:inline-flex" title="Übersicht">
          <Icon name="chart" size={15} />
        </button>
        <button onClick={toggleDark} className="btn-ghost" title={darkMode ? 'Light Mode' : 'Dark Mode'}>
          <Icon name={darkMode ? 'sun' : 'moon'} size={15} />
        </button>
        <button onClick={() => openModal('settings')} className="btn-ghost" title="Einstellungen">
          <Icon name="settings" size={15} />
        </button>
      </div>
    </header>
  );
}

function getKW(d: Date) {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const jan4 = new Date(target.getFullYear(), 0, 4);
  const dayDiff = (target.getTime() - jan4.getTime()) / 86400000;
  return 1 + Math.ceil((dayDiff - 3 + ((jan4.getDay() + 6) % 7)) / 7);
}
